/**
 * CostPage.js
 * ===========
 * Cost reporting page — wired to /api/v1/cost/* endpoints.
 * Drop into src/pages/CostPage.js and add to your router/nav.
 */

import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';

// ─── Helpers ─────────────────────────────────────────────────
const fmt   = (n, d=0) => n == null ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits:d, maximumFractionDigits:d })}`;
const pct   = (n)      => n == null ? '—' : `${Number(n).toFixed(1)}%`;
const fmtMo = (iso)    => { const d = new Date(iso); return d.toLocaleDateString('en-GB', { month:'short', year:'numeric' }); };

// Generate list of last 13 months as { value: 'YYYY-MM-01', label: 'Mon YYYY' }
function getMonthOptions() {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 13; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`;
    const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
}
const MONTH_OPTIONS = getMonthOptions();

// ─── Styles (inline — matches existing dark theme) ────────────
const S = {
  page:      { padding: '24px' },
  grid4:     { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:16 },
  grid2:     { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 },
  card:      { background:'#161b22', border:'1px solid #21262d', borderRadius:8, padding:20 },
  cardTitle: { fontFamily:'monospace', fontSize:11, color:'#8b949e', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 },
  bigNum:    { fontFamily:'monospace', fontSize:22, fontWeight:700 },
  sub:       { fontFamily:'sans-serif', fontSize:11, color:'#8b949e', marginTop:2 },
  row:       { display:'grid', alignItems:'center', gap:8, padding:'8px 4px', borderTop:'1px solid #21262d' },
  rowCust:   { display:'grid', gridTemplateColumns:'90px 1fr 100px 100px 90px 90px 70px', alignItems:'center', gap:8, padding:'8px 4px', borderTop:'1px solid #21262d', cursor:'pointer' },
  pill:      (c='#3fb950') => ({ display:'inline-block', fontFamily:'monospace', fontSize:10, color:c, background:`${c}18`, border:`1px solid ${c}40`, borderRadius:10, padding:'1px 8px' }),
  tag:       (c='#8b949e') => ({ fontFamily:'monospace', fontSize:10, color:c, background:`${c}18`, border:`1px solid ${c}30`, borderRadius:4, padding:'2px 6px' }),
  bar:       { background:'#21262d', borderRadius:3, height:8, overflow:'hidden', display:'flex' },
  hdr:       { fontFamily:'monospace', fontSize:9, color:'#484f58', textTransform:'uppercase', letterSpacing:'0.08em' },
  btn:       (active) => ({
    fontFamily:'monospace', fontSize:10, letterSpacing:'0.06em',
    background: active ? '#1c2330' : 'transparent',
    color: active ? '#e6edf3' : '#8b949e',
    border:`1px solid ${active ? '#30363d' : '#21262d'}`,
    borderRadius:4, padding:'4px 10px', cursor:'pointer'
  }),
  error:     { color:'#f85149', fontFamily:'monospace', fontSize:13, padding:16 },
  loading:   { color:'#8b949e', fontFamily:'monospace', fontSize:13, padding:16 },
  backBtn:   { fontFamily:'monospace', fontSize:11, color:'#58a6ff', background:'transparent', border:'none', cursor:'pointer', padding:'0 0 12px 0', display:'block' },
};

// ─── Sub-components ───────────────────────────────────────────

function KpiCard({ label, value, sub, color='#e6edf3' }) {
  return (
    <div style={S.card}>
      <div style={S.cardTitle}>{label}</div>
      <div style={{ ...S.bigNum, color }}>{value}</div>
      {sub && <div style={S.sub}>{sub}</div>}
    </div>
  );
}

function StackedBar({ net, gross, edp, height=8 }) {
  const sp    = Math.max(gross - net - edp, 0);
  const total = gross || 1;
  return (
    <div style={{ ...S.bar, height }}>
      <div style={{ flex: net/total,  background:'#58a6ff' }} />
      <div style={{ flex: sp/total,   background:'#3fb950', opacity:0.7 }} />
      <div style={{ flex: edp/total,  background:'#bc8cff', opacity:0.7 }} />
    </div>
  );
}

function TrendChart({ data }) {
  if (!data?.length) return <div style={S.loading}>No trend data</div>;
  const maxG = Math.max(...data.map(d => d.cost_gross), 1);
  return (
    <div>
      <div style={{ display:'flex', gap:6, alignItems:'flex-end', height:100 }}>
        {data.map((m, i) => {
          const netH  = Math.round(m.cost_net    / maxG * 90);
          const savH  = Math.round(m.cost_saving / maxG * 90);
          return (
            <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
              <div style={{ fontFamily:'monospace', fontSize:8, color:'#484f58' }}>{fmt(m.cost_net,0)}</div>
              <div style={{ width:'100%', display:'flex', flexDirection:'column', justifyContent:'flex-end', height:80 }}>
                <div style={{ width:'100%', background:'#3fb950', opacity:0.6, height:`${savH}%` }} />
                <div style={{ width:'100%', background:'#58a6ff', height:`${netH}%` }} />
              </div>
              <div style={{ fontFamily:'monospace', fontSize:8, color:'#8b949e' }}>{fmtMo(m.month).split(' ')[0]}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop:8, display:'flex', gap:12, fontFamily:'monospace', fontSize:10, color:'#8b949e' }}>
        <span><span style={{color:'#58a6ff'}}>■</span> Net</span>
        <span><span style={{color:'#3fb950'}}>■</span> Savings</span>
      </div>
    </div>
  );
}

// ─── Customer Detail Drilldown ────────────────────────────────

function CustomerDetail({ code, onBack }) {
  const [data, setData]     = useState(null);
  const [months, setMonths] = useState(6);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/v1/cost/customers/${code}?months=${months}`)
      .then(r => { setData(r.data); setError(null); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [code, months]);

  if (loading) return <div style={S.loading}>Loading…</div>;
  if (error)   return <div style={S.error}>Error: {error}</div>;
  if (!data)   return null;

  const latest = data.trend[data.trend.length - 1] || {};

  return (
    <div>
      <button style={S.backBtn} onClick={onBack}>← Back to all customers</button>

      <div style={{ fontFamily:'monospace', fontSize:16, color:'#e6edf3', marginBottom:16 }}>
        {data.customer_code} &nbsp;<span style={{ color:'#8b949e', fontSize:13, fontFamily:'sans-serif' }}>{data.cust_name}</span>
      </div>

      {/* Month range picker */}
      <div style={{ display:'flex', gap:6, marginBottom:16 }}>
        {[3,6,12].map(m => (
          <button key={m} style={S.btn(months===m)} onClick={()=>setMonths(m)}>{m}M</button>
        ))}
      </div>

      {/* KPIs for latest month */}
      <div style={S.grid4}>
        <KpiCard label="Net Cost"    value={fmt(latest.cost_net,0)}         sub="latest month"            color='#58a6ff' />
        <KpiCard label="Gross Cost"  value={fmt(latest.cost_gross,0)}       sub="before discounts"        color='#8b949e' />
        <KpiCard label="SP Saving"   value={fmt(latest.cost_sp_saving,0)}   sub="Savings Plans"           color='#3fb950' />
        <KpiCard label="EDP Saving"  value={fmt(latest.cost_edp,0)}         sub={pct(latest.saving_pct) + ' total'} color='#bc8cff' />
      </div>

      {/* Trend chart */}
      <div style={{ ...S.card, marginBottom:16 }}>
        <div style={S.cardTitle}>Monthly Trend</div>
        <TrendChart data={data.trend} />
      </div>

      {/* Region breakdown */}
      <div style={S.card}>
        <div style={S.cardTitle}>Latest Month — By Region</div>
        <div style={{ marginTop:12 }}>
          {/* Header */}
          <div style={{ display:'grid', gridTemplateColumns:'160px 1fr 100px 100px 90px 90px', gap:8, paddingBottom:6 }}>
            {['Region','','Gross','Net','SP Saved','EDP'].map(h => (
              <div key={h} style={S.hdr}>{h}</div>
            ))}
          </div>
          {data.regions.map(r => (
            <div key={r.region} style={{ display:'grid', gridTemplateColumns:'160px 1fr 100px 100px 90px 90px', gap:8, alignItems:'center', padding:'8px 0', borderTop:'1px solid #21262d' }}>
              <span style={S.tag()}>{r.region}</span>
              <StackedBar net={r.cost_net} gross={r.cost_gross} edp={r.cost_edp} />
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e', textAlign:'right' }}>{fmt(r.cost_gross,0)}</div>
              <div style={{ fontFamily:'monospace', fontSize:12, color:'#58a6ff', textAlign:'right', fontWeight:600 }}>{fmt(r.cost_net,0)}</div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#3fb950', textAlign:'right' }}>{fmt(r.cost_sp_saving,0)}</div>
              <div style={{ fontFamily:'monospace', fontSize:11, color:'#bc8cff', textAlign:'right' }}>{fmt(r.cost_edp,0)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Cost Page ───────────────────────────────────────────

export default function CostPage() {
  const [summary, setSummary]       = useState(null);
  const [customers, setCustomers]   = useState([]);
  const [trend, setTrend]           = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[1].value); // default: last full month
  const [viewMode, setViewMode]     = useState('summary');
  const [selected, setSelected]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/api/v1/cost/account/summary?month=${selectedMonth}`),
      api.get(`/api/v1/cost/account/all?month=${selectedMonth}`),
      api.get('/api/v1/cost/trend?months=12'),
    ])
      .then(([s, c, t]) => {
        setSummary(s.data);
        setCustomers(c.data);
        setTrend(t.data);
        setError(null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedMonth]);

  useEffect(() => { load(); }, [load]);

  // ── Customer drilldown ──
  if (selected) {
    return (
      <div style={S.page}>
        <CustomerDetail code={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  if (loading) return <div style={S.loading}>Loading cost data…</div>;
  if (error)   return <div style={S.error}>Error loading cost data: {error}</div>;

  const maxGross = Math.max(...customers.map(c => c.cost_gross), 1);

  return (
    <div style={S.page}>

      {/* Controls */}
      <div style={{ display:'flex', gap:12, marginBottom:16, alignItems:'center' }}>
        <span style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e' }}>Month:</span>
        <select
          value={selectedMonth}
          onChange={e => setSelectedMonth(e.target.value)}
          style={{
            fontSize:13, padding:'6px 10px', border:'1px solid #21262d',
            borderRadius:6, background:'#161b22', color:'#e6edf3',
            fontFamily:'monospace', cursor:'pointer', minWidth:180
          }}
        >
          {MONTH_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <div style={{ marginLeft:'auto', display:'flex', gap:6 }}>
          {['summary','trend'].map(v => (
            <button key={v} style={S.btn(viewMode===v)} onClick={()=>setViewMode(v)}>{v.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div style={S.grid4}>
        <KpiCard label="Total Net Cost"    value={fmt(summary?.total_net,0)}      sub="after all discounts"                     color='#58a6ff' />
        <KpiCard label="Gross (List) Cost" value={fmt(summary?.total_gross,0)}    sub="before discounts"                        color='#8b949e' />
        <KpiCard label="SP Savings"        value={fmt(summary?.tagged_saving,0)}  sub="Savings Plans"                           color='#3fb950' />
        <KpiCard label="EDP Savings"       value={fmt(summary?.tagged_edp,0)}     sub={`${pct(summary?.saving_pct)} total saved`} color='#bc8cff' />
      </div>

      {viewMode === 'trend' ? (
        <div style={S.card}>
          <div style={S.cardTitle}>12-Month Cost Trend — All Customers</div>
          <div style={{ marginTop:16 }}>
            <TrendChart data={trend} />
          </div>
        </div>
      ) : (
        <>
          {/* EDP / tagging info */}
          <div style={{ ...S.card, borderColor:'#bc8cff44', background:'#bc8cff08', marginBottom:16 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:16 }}>
              <div>
                <div style={S.cardTitle}>EDP Attribution</div>
                <div style={{ fontFamily:'sans-serif', fontSize:12, color:'#8b949e', lineHeight:1.5 }}>
                  Proportionally allocated by share of gross spend. EDP is an account-level discount applied via the BIL profile.
                </div>
              </div>
              <div>
                <div style={S.cardTitle}>Tagging Coverage</div>
                <div style={{ fontFamily:'monospace', fontSize:20, color: (summary?.untagged_pct||0) > 10 ? '#d29922' : '#3fb950', fontWeight:700 }}>
                  {(100 - (summary?.untagged_pct||0)).toFixed(1)}%
                </div>
                <div style={S.sub}>{(summary?.untagged_pct||0).toFixed(1)}% spend unattributed</div>
              </div>
              <div>
                <div style={S.cardTitle}>Customers Tracked</div>
                <div style={{ fontFamily:'monospace', fontSize:20, color:'#e6edf3', fontWeight:700 }}>{summary?.customer_count || 0}</div>
                <div style={S.sub}>with Client tag in CE</div>
              </div>
            </div>
          </div>

          {/* Per-customer table */}
          <div style={S.card}>
            <div style={S.cardTitle}>Cost by Customer</div>
            {/* Legend */}
            <div style={{ display:'flex', gap:12, fontFamily:'monospace', fontSize:10, color:'#8b949e', margin:'8px 0 12px' }}>
              <span><span style={{color:'#58a6ff'}}>■</span> Net</span>
              <span><span style={{color:'#3fb950'}}>■</span> SP saving</span>
              <span><span style={{color:'#bc8cff'}}>■</span> EDP saving</span>
            </div>
            {/* Header */}
            <div style={{ display:'grid', gridTemplateColumns:'90px 1fr 100px 100px 90px 90px 70px', gap:8 }}>
              {['Customer','','Gross','Net','SP Saved','EDP','Saving'].map(h => (
                <div key={h} style={S.hdr}>{h}</div>
              ))}
            </div>
            {customers.map(c => (
              <div key={c.customer_code}
                style={{ ...S.rowCust }}
                onClick={() => setSelected(c.customer_code)}
                onMouseEnter={e => e.currentTarget.style.background='#1c2330'}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}
              >
                <div style={{ fontFamily:'monospace', fontSize:12, color:'#e6edf3' }}>{c.customer_code}</div>
                <StackedBar net={c.cost_net} gross={c.cost_gross} edp={c.cost_edp} />
                <div style={{ fontFamily:'monospace', fontSize:11, color:'#8b949e',  textAlign:'right' }}>{fmt(c.cost_gross,0)}</div>
                <div style={{ fontFamily:'monospace', fontSize:12, color:'#58a6ff',  textAlign:'right', fontWeight:600 }}>{fmt(c.cost_net,0)}</div>
                <div style={{ fontFamily:'monospace', fontSize:11, color:'#3fb950',  textAlign:'right' }}>{fmt(c.cost_sp_saving,0)}</div>
                <div style={{ fontFamily:'monospace', fontSize:11, color:'#bc8cff',  textAlign:'right' }}>{fmt(c.cost_edp,0)}</div>
                <span style={S.pill()}>{pct(c.saving_pct)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
